import React from 'react';
import ResearchLockedNotice from './ResearchLockedNotice';
import { RESEARCH_ALL_LOCKED } from '../config/researchFeatures';
import './SpySelectorPopup.css';

const TOOL_ICONS = {
  web_intelligence: 'fa fa-globe',
  instagram_research: 'fab fa-instagram',
  website_analyzer: 'fa fa-laptop-code',
  seo_analyzer: 'fa fa-magnifying-glass-chart',
  potential_analyzer: 'fa fa-bullseye',
};

/**
 * Zwei unabhängige Gründe, warum ein Tool nicht startet:
 *
 *   locked     — der Account hat keine Lizenz (Admin-Freischaltung fehlt).
 *                Karte bleibt sichtbar, ausgegraut, mit Anfrage-Button.
 *   available  — Lizenz da, aber die Voraussetzungen fehlen (kein Website-URL,
 *                kein Sistrix-Key, Tool in den Settings aus).
 */
const SpySelectorPopup = ({
  isOpen,
  onClose,
  onSelectSpy,
  contactData,
  isLoading,
  research = RESEARCH_ALL_LOCKED,
  researchLoading = false,
  researchContext = {},
  seoKeyReady,
  potentialEnabled,
  analyzerCreditCost,
}) => {
  if (!isOpen) return null;

  const hasBusinessName = contactData?.business_name?.trim();
  const hasWebsite = contactData?.lead_website?.trim();
  const canAnalyze = hasBusinessName || hasWebsite;

  const rawIg = contactData?.lead_instagram?.trim() || '';
  const igHandle = rawIg
    .replace(/^https?:\/\/(www\.)?instagram\.com\//, '')
    .replace(/^@/, '')
    .replace(/[?#].*$/, '')
    .replace(/\/+$/, '')
    .trim();
  const hasInstagram = igHandle.length > 0;

  const contactLabel = hasBusinessName || hasWebsite || 'Contact';
  const contactDomain = hasWebsite
    ? hasWebsite.replace(/^https?:\/\/(www\.)?/, '').replace(/\/.*$/, '')
    : null;

  const tools = [
    {
      id: 'web_intelligence',
      name: 'Web Research',
      locked: !research.hasWebResearch,
      description: 'Website, Google presence & social profiles',
      credits: '3–8',
      available: canAnalyze,
      disabledReason: !canAnalyze ? 'Business name or website URL required' : null,
      features: ['Website', 'Google', 'Social Media'],
    },
    {
      id: 'website_analyzer',
      name: 'Website Analyzer',
      locked: !research.hasWebsiteAnalyzer,
      description: hasWebsite ? `Scan ${contactDomain || 'website'} for CMS & shop data` : 'Website URL required',
      credits: String(analyzerCreditCost || '1–4'),
      available: !!hasWebsite,
      disabledReason: !hasWebsite ? 'Website URL required (lead_website)' : null,
      features: ['CMS Detection', 'Product Count', 'Website Age'],
    },
    {
      id: 'instagram_research',
      name: 'Instagram Research',
      locked: !research.hasInstagramResearch,
      description: hasInstagram ? `Latest posts from @${igHandle}` : 'Instagram handle required',
      credits: '3',
      available: hasInstagram,
      disabledReason: !hasInstagram ? 'Instagram handle required (lead_instagram)' : null,
      features: ['Posts', 'Engagement', 'Content'],
    },
    {
      id: 'seo_analyzer',
      name: 'SEO Analyzer',
      locked: !research.hasSeoAnalyzer,
      // Freigeschaltet, aber ohne hinterlegten Sistrix-Key läuft der Scan nicht.
      description: !seoKeyReady
        ? 'Sistrix API key required — add it in Research Settings'
        : (hasWebsite ? `Visibility & keywords for ${contactDomain || 'website'}` : 'Website URL required'),
      credits: null,
      available: !!hasWebsite && !!seoKeyReady,
      disabledReason: !seoKeyReady
        ? 'Sistrix API key missing (Research Settings)'
        : (!hasWebsite ? 'Website URL required (lead_website)' : null),
      features: ['Visibility', 'Keywords', 'Competitors'],
    },
    {
      id: 'potential_analyzer',
      name: 'Potential Analyser',
      locked: !research.hasPotentialAnalyzer,
      description: !potentialEnabled
        ? 'Enable it in Research Settings'
        : 'Bewertet 1–10, wie gut dieser Kontakt zu deinem Wunschkunden passt',
      credits: '2',
      available: canAnalyze && !!potentialEnabled,
      disabledReason: !potentialEnabled
        ? 'Potential Analyser is switched off in Research Settings'
        : (!canAnalyze ? 'Business name or website URL required' : null),
      features: ['1–10 Fit-Score', 'Größen-Check', 'Begründung'],
    },
  ];

  return (
    <div className="rsp-overlay" onClick={onClose}>
      <div className="rsp-popup" onClick={(e) => e.stopPropagation()}>

        <div className="rsp-header">
          <div className="rsp-header-top">
            <div className="rsp-brand">
              <img src="/img/salesgod_dark.webp" alt="BonusClub" className="rsp-brand-logo" />
              <span className="rsp-brand-label">Research Lab</span>
            </div>
            <button className="rsp-close" onClick={onClose} aria-label="Close">
              <i className="fa fa-times" />
            </button>
          </div>
          <div className="rsp-contact-row">
            <span className="rsp-contact-name">{contactLabel}</span>
            {contactDomain && <span className="rsp-contact-domain">{contactDomain}</span>}
          </div>
        </div>

        <div className="rsp-body">
          {tools.map((tool) => {
            // Solange die Lizenzen laden, wird nicht gesperrt dargestellt —
            // sonst blitzt beim Öffnen kurz "not available" auf.
            const locked = !researchLoading && tool.locked;

            // Gesperrte Karten sind bewusst <div> statt <button>: Sie sind nicht
            // klickbar und enthalten den Mail-Link, der in einem Button
            // ungültiges Markup wäre.
            if (locked) {
              return (
                <div key={tool.id} className="rsp-tool rsp-tool--locked">
                  <div className={`rsp-tool-icon ${tool.id === 'instagram_research' ? 'rsp-tool-icon--ig' : ''}`}>
                    <i className={TOOL_ICONS[tool.id]} />
                  </div>
                  <div className="rsp-tool-content">
                    <div className="rsp-tool-top">
                      <span className="rsp-tool-name">{tool.name}</span>
                      <span className="rsp-tool-credits rsp-tool-credits--locked">
                        <i className="fa fa-lock" /> Locked
                      </span>
                    </div>
                    <ResearchLockedNotice
                      featureName={tool.name}
                      accountEmail={researchContext.accountEmail}
                      worldId={researchContext.worldId}
                    />
                  </div>
                </div>
              );
            }

            const usable = tool.available && !researchLoading;

            return (
              <button
                key={tool.id}
                className={`rsp-tool ${!usable ? 'rsp-tool--disabled' : ''} ${isLoading ? 'rsp-tool--loading' : ''}`}
                onClick={() => usable && !isLoading && onSelectSpy(tool.id)}
                disabled={!usable || isLoading}
                title={tool.disabledReason || ''}
              >
                <div className={`rsp-tool-icon ${tool.id === 'instagram_research' ? 'rsp-tool-icon--ig' : ''}`}>
                  {isLoading ? <div className="rsp-spinner" /> : <i className={TOOL_ICONS[tool.id]} />}
                </div>
                <div className="rsp-tool-content">
                  <div className="rsp-tool-top">
                    <span className="rsp-tool-name">{tool.name}</span>
                    <span className="rsp-tool-credits">{tool.credits ? `${tool.credits} Credits` : 'Sistrix key'}</span>
                  </div>
                  <span className="rsp-tool-desc">
                    {isLoading ? 'Analyzing...' : tool.description}
                  </span>
                  {tool.features && !isLoading && (
                    <div className="rsp-tool-tags">
                      {tool.features.map((f) => (
                        <span key={f} className="rsp-tag">
                          <i className="fa fa-check" />{f}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="rsp-tool-arrow">
                  <i className={`fa ${isLoading ? 'fa-spinner fa-spin' : 'fa-arrow-right'}`} />
                </div>
              </button>
            );
          })}

          <div className="rsp-tool rsp-tool--preview">
            <div className="rsp-tool-icon rsp-tool-icon--competitor">
              <i className="fa fa-crosshairs" />
            </div>
            <div className="rsp-tool-content">
              <div className="rsp-tool-top">
                <span className="rsp-tool-name">Competitor Finder</span>
                <span className="rsp-tool-credits">Coming April 1.</span>
              </div>
              <span className="rsp-tool-desc">
                Find up to 5 competitors & import them into SalesGod
              </span>
              <div className="rsp-tool-tags">
                <span className="rsp-tag"><i className="fa fa-check" />Up to 5 Competitors</span>
                <span className="rsp-tag"><i className="fa fa-check" />Auto Import</span>
                <span className="rsp-tag"><i className="fa fa-check" />Industry Match</span>
              </div>
            </div>
            <div className="rsp-tool-arrow">
              <i className="fa fa-arrow-right" />
            </div>
          </div>

          <div className="rsp-tool rsp-tool--preview">
            <div className="rsp-tool-icon rsp-tool-icon--whatsapp">
              <i className="fab fa-whatsapp" />
            </div>
            <div className="rsp-tool-content">
              <div className="rsp-tool-top">
                <span className="rsp-tool-name">WhatsApp Number Checker</span>
                <span className="rsp-tool-credits">Coming Soon</span>
              </div>
              <span className="rsp-tool-desc">
                Check if a phone number is registered on WhatsApp
              </span>
              <div className="rsp-tool-tags">
                <span className="rsp-tag"><i className="fa fa-check" />Number Validation</span>
                <span className="rsp-tag"><i className="fa fa-check" />WhatsApp Status</span>
                <span className="rsp-tag"><i className="fa fa-check" />Bulk Check</span>
              </div>
            </div>
            <div className="rsp-tool-arrow">
              <i className="fa fa-arrow-right" />
            </div>
          </div>

        </div>

        <div className="rsp-footer">
          <i className="fa fa-lock" />
          <span>Encrypted & stored in your database</span>
        </div>
      </div>
    </div>
  );
};

export default SpySelectorPopup;
